import React, { memo, useCallback } from 'react';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import { IconDocument } from 'design-system/icons';
import { CardView, rem, Spacer } from 'design-system';
import CardRowContainer from 'design-system/CardRowContainer';
import CardRowTitleWithToolTip from 'design-system/CardRowTitleWithToolTip';
import CardUpgradeRow from 'design-system/CardUpgradeRow';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectIsUserUltimate } from 'features/premium/selectors/tier';
import { formatAmount, formatPercentageToMinimum2DecimalPlaces } from 'utils';
import AboutInfoRow from './AboutInfoRow';
import SectionHeader from './SectionHeader';
import FundAllocationSection from './FundAllocationSection';
import { isAndroid } from '../../../constants';
import { commonToolTipStrings } from '../constants';
const StockAboutSectionSeccl = ({ assetId, esgUrl, kidUrl, fundName, fundRiskLevel, platformFee, tradingCharge, ongoingCharge, transactionCost, incidentalCost, isUserOnboardingInvest, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const showUpgradeRow = !useAppSelector(selectIsUserUltimate);
    const openKid = useCallback(() => {
        if (kidUrl) {
            if (isAndroid) {
                navigation.navigateDeprecated('Pdf', { url: kidUrl });
            }
            else {
                Links.openLink(kidUrl);
            }
        }
    }, [kidUrl]);
    const openEsg = useCallback(() => {
        if (esgUrl) {
            if (isAndroid) {
                navigation.navigateDeprecated('Pdf', { url: esgUrl });
            }
            else {
                Links.openLink(esgUrl);
            }
        }
    }, [esgUrl]);
    return (<>
      <SectionHeader title="About"/>
      <CardView noMarginTop noMarginBottom>
        <CardRowContainer>
          <AboutInfoRow tableSpacing title="Fund name" info={fundName} descNumberOfLines={2}/>
          {typeof fundRiskLevel === 'number' && (<AboutInfoRow tableSpacing title="Risk level" info={`${fundRiskLevel}/7`}/>)}
        </CardRowContainer>
      </CardView>
      <SectionHeader title="Charges"/>
      <CardView noMarginTop noMarginBottom>
        <CardRowContainer>
          {typeof tradingCharge === 'number' && (<AboutInfoRow tableSpacing title={<CardRowTitleWithToolTip title="Trading charge" toolTipTitle={commonToolTipStrings.tradingChargeTitle} toolTipDescription={commonToolTipStrings.tradingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(tradingCharge, 'GBP', 2)}</Text>} descNumberOfLines={2}/>)}
          {typeof ongoingCharge === 'number' && (<AboutInfoRow tableSpacing title={<CardRowTitleWithToolTip title="Ongoing charges figure" toolTipTitle={commonToolTipStrings.ongoingChargeTitle} toolTipDescription={commonToolTipStrings.ongoingChargeDesc}/>} info={<Text Numbers-14>{formatPercentageToMinimum2DecimalPlaces(ongoingCharge)}</Text>} descNumberOfLines={2}/>)}
          {typeof transactionCost === 'number' && (<AboutInfoRow tableSpacing title={<CardRowTitleWithToolTip title="Transaction cost (est.)" toolTipTitle={commonToolTipStrings.transactionCostTitle} toolTipDescription={commonToolTipStrings.transactionCostDesc}/>} info={<Text Numbers-14>{formatPercentageToMinimum2DecimalPlaces(transactionCost)}</Text>} descNumberOfLines={2}/>)}
          {typeof incidentalCost === 'number' && (<AboutInfoRow tableSpacing title={<CardRowTitleWithToolTip title="Incidental cost (est.)" toolTipTitle={commonToolTipStrings.incidentalCostTitle} toolTipDescription={commonToolTipStrings.incidentalCostDesc}/>} info={<Text Numbers-14>{formatPercentageToMinimum2DecimalPlaces(incidentalCost)}</Text>} descNumberOfLines={2}/>)}
          {typeof platformFee === 'number' && (<AboutInfoRow tableSpacing title={<CardRowTitleWithToolTip title="AUM fee" toolTipTitle={commonToolTipStrings.platformFeeTitle} toolTipDescription={commonToolTipStrings.platformFeeDesc}/>} info={<Text Numbers-14>{formatPercentageToMinimum2DecimalPlaces(platformFee)}</Text>} descNumberOfLines={2}/>)}
        </CardRowContainer>
        {showUpgradeRow && !isUserOnboardingInvest ? (<>
            <CardUpgradeRow title="Want lower AUM fee?" withHorizontalMargins/>
            <Spacer h={16}/>
          </>) : null}
      </CardView>
      {assetId ? <FundAllocationSection assetId={assetId}/> : null}
      {(!!kidUrl || !!esgUrl) && (<>
          <SectionHeader title="Documents"/>
          <CardView noMarginTop noMarginBottom>
            {!!esgUrl && (<TouchableHighlight hoverOnLight activeOpacity={1} onPress={openEsg} underlayColor={colors.background.underlay}>
                <View style={styles.documentBtn}>
                  <View style={styles.iconContainer}>
                    <IconDocument colorKey="brand"/>
                  </View>
                  <Spacer w={16}/>
                  <View style={styles.flex}>
                    <Text Text-16>Consumer facing disclosure</Text>
                    <Text TextThin-14 Primary>
                      Review
                    </Text>
                  </View>
                </View>
              </TouchableHighlight>)}
            {!!kidUrl && (<TouchableHighlight hoverOnLight activeOpacity={1} underlayColor={colors.background.underlay} onPress={openKid}>
                <View style={styles.documentBtn}>
                  <View style={styles.iconContainer}>
                    <IconDocument colorKey="brand"/>
                  </View>
                  <Spacer w={16}/>
                  <View style={styles.flex}>
                    <Text Text-16>Key information document</Text>
                    <Text TextThin-14 Primary>
                      Review
                    </Text>
                  </View>
                </View>
              </TouchableHighlight>)}
          </CardView>
        </>)}
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    documentBtn: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    iconContainer: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(12),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.brandLight,
    },
    tradingChargeTitleContainer: {
        flexDirection: 'row',
    },
}));
export default memo(StockAboutSectionSeccl);
